import { resolveSafeRedirect } from '@/lib/auth/safe-redirect'

/**
 * Did this visit to /login come from a page the person could no longer open
 * because they had no session?
 *
 * The proxy sends every page request without a session cookie to
 * `/login?next=<that page>` (proxy.ts, "All other routes require
 * authentication"), and the cookie lapses with the session — so a `next`
 * naming a page on this site is the mark of a lapsed (or not yet started)
 * session. Not: a plain visit, the site root (`next=/` is just "the app"),
 * the invite round trip (`next=/accept-invite…`, which explains itself), or a
 * `next` that is not an address on this site (the same rule every post-sign-in
 * redirect uses).
 */
export function landedFromLapsedSession(next: string | null | undefined): boolean {
  const target = resolveSafeRedirect(next, '')
  if (!target) return false
  const path = target.split(/[?#]/)[0]
  if (path === '/' || path === '/login' || path.startsWith('/login/')) return false
  if (path === '/accept-invite' || path.startsWith('/accept-invite/')) return false
  return true
}

/**
 * Pages a session that ends never sends the person BACK to through the
 * customer sign-in:
 *   - the sign-in pages themselves — returning to one loops, or nests one
 *     `next` inside another;
 *   - the realms with their own sign-in — the employee portal, the PhishTrainer
 *     players, the SCORM player, the super-admin console — which keep the
 *     bare /login they had (the API client does not even sign them out);
 *   - the integration consent callback, whose one-time code is spent.
 * Matched by whole path segment: `/portal` is not `/portal-theme-preview`.
 */
const NO_RETURN_PATHS = [
  '/login',
  '/auth',
  '/verify-2fa',
  '/setup-2fa',
  '/accept-invite',
  '/access-denied',
  '/portal',
  '/play',
  '/phishtrainer/play',
  '/player',
  '/admin',
  '/connect',
]

/** Longer than this and the query stays behind; the page still goes. */
const MAX_NEXT = 2048

/**
 * Where a session that ENDED — the API refused it and could not renew it —
 * sends the person: `/login?next=<the page they were on, query included>`, so
 * the sign-in page shows the session-ended picture (landedFromLapsedSession,
 * above) and signing in returns them to the page. A bare `/login` when there is
 * no page worth returning to (NO_RETURN_PATHS, the site root), and always when
 * the person signed out on purpose — that path never calls this.
 *
 * The `next` written here passes the same check every reader applies
 * (resolveSafeRedirect), so this never writes one a reader would refuse.
 */
export function sessionEndedLoginHref(
  location: { pathname: string; search?: string } | null | undefined,
): string {
  if (!location) return '/login'
  const { pathname } = location
  if (pathname === '/') return '/login'
  if (NO_RETURN_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return '/login'
  let next = `${pathname}${location.search ?? ''}`
  if (next.length > MAX_NEXT) next = pathname
  if (resolveSafeRedirect(next, '') !== next) return '/login'
  return `/login?next=${encodeURIComponent(next)}`
}
